import React from "react"

import { useAuth } from "@/contexts/AuthContext"

interface PermissionGuardProps {
  children: React.ReactNode
  resource: string
  action?: string
  scopes?: ("any" | "own")[]
  fallback?: React.ReactNode
}

/**
 * PermissionGuard component that checks if the user has the required permission
 * for a specific resource and action. If they don't have permission, it shows
 * the fallback component or null.
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  resource,
  action = "read",
  scopes = ["any"],
  fallback = null,
}) => {
  const { claims } = useAuth()

  // Check if user has the required permission
  const hasPermission = () => {
    if (claims.user_role === "Admin") return true
    if (!claims?.role_permissions) return false

    // Build all possible permission combinations
    const possiblePermissions = new Set<string>()

    for (const scope of scopes) {
      // Add exact permission: action.scope.resource
      possiblePermissions.add(`${action}.${scope}.${resource}`)
    }

    // Add broader permission: action.any.resource (covers both "any" and "own" scopes)
    possiblePermissions.add(`${action}.any.${resource}`)

    // Check if user has ANY of these permissions
    return Array.from(possiblePermissions).some((permission) =>
      claims.role_permissions.includes(permission),
    )
  }

  if (!hasPermission()) {
    return <>{fallback}</>
  }

  return <>{children}</>
}
